import { Theme } from '@emotion/react'
import styled from '@emotion/styled'
import { withClass } from '@news-mono/web-common'
import { Button } from '../../../../../../buttons/Button/Button'
import { colors } from '../../../../../../__styling/settings/colors'
import { breakpointMax } from '../../../../../../__styling/style-functions'
import { calcRem } from '../../../../../../__styling/style-functions/calc-rem'
import {
    fluidTypography,
    miniDivider,
} from '../../../../../../__styling/style-mixins'
import { themedValue } from '../../../../../../__styling/themed-value'
import { PackageTheme } from '../../../../common'

export interface PackageThemeProps {
    packageTheme: PackageTheme
}

export const StyledSubscriptionPackage = styled('div')<PackageThemeProps>(
    ({ theme }) => ({
        flexBasis: 200,
        flexShrink: 1,
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        padding: theme.margins.md,
        margin: calcRem(theme.margins.md, 0),
        borderRadius: 6,
        fontFamily: theme.fonts.subscriber.text,
        backgroundColor: colors.white,
        color: themedValue(theme, {
            thewest: colors.thewest.westblue,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        boxShadow: '0 2px 21px 0 rgba(0,0,0,0.5)',

        '&:last-child': {
            marginLeft: calcRem(theme.margins.md),
        },
    }),
)

export const StyledSubscriptionPackagePreTitle = styled(
    'h3',
)<PackageThemeProps>(({ theme, packageTheme }) => ({
    textTransform: 'uppercase',
    fontSize: calcRem(12),
    fontWeight: 700,
    position: 'relative',
    color: packageTheme === 'red' ? colors.thewest.ladybird : undefined,

    // pseudo-element divider
    ...miniDivider({
        theme: theme,
        dividerColor: themedValue(theme, {
            thewest:
                packageTheme === 'red'
                    ? colors.thewest.ladybird
                    : colors.thewest.westblue,
            sevennews: colors.sevennews.red,
            perthnow: colors.perthnow.pinkThulite,
            fallback: '',
        }),
        spacing: theme.margins.sm,
    }),
}))

export const StyledPackageImage = styled('img')(() => ({
    width: '100%',
    display: 'block',
    margin: '0 auto',
    transform: 'scale(1.1)',
    minHeight: '150px',
    objectFit: 'contain',
}))

export const StyledSubscriptionPackageHeading = styled('h4')<PackageThemeProps>(
    ({ theme }) => ({
        fontSize: calcRem(20),
        fontWeight: 700,
        lineHeight: 1.167,
        margin: calcRem(0, 0, theme.margins.sm, 0),
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),

        flex: '0 0 auto', // fixes ie11 column wrap bug

        [breakpointMax('xs')]: {
            fontSize: calcRem(16),
        },
    }),
)

export const StyledSubscriptionPackageOriginalPrice = styled(
    'h4',
)<PackageThemeProps>(({ theme }) => ({
    fontSize: calcRem(16),
    fontWeight: 700,
    lineHeight: 1.167,
    textDecoration: 'line-through',
    margin: 0,
    color: themedValue(theme, {
        thewest: colors.thewest.greyGorilla,
        perthnow: colors.perthnow.greyBasalt90,
        sevennews: colors.sevennews.charade,
        fallback: undefined,
    }),

    flex: '0 0 auto', // fixes ie11 column wrap bug

    [breakpointMax('xs')]: {
        fontSize: calcRem(16),
    },
}))

const themedPriceFluid = (theme: Theme) =>
    themedValue(theme, {
        sevennews: fluidTypography(375, 1000, 24, 28),
        perthnow: fluidTypography(375, 1020, 24, 30),
        thewest: fluidTypography(375, 1220, 24, 26),
        fallback: undefined,
    })

export const StyledSubscriptionPackagePrice = styled('p')<PackageThemeProps>(
    ({ theme, packageTheme }) => ({
        ...themedPriceFluid(theme),
        flex: '0 0 auto', // fixes ie11 column wrap bug
        fontWeight: 700,
        lineHeight: 1.167,
        margin: 0,
        color: packageTheme === 'red' ? colors.thewest.ladybird : undefined,
    }),
)

export const StyledSubscriptionPackagePriceQualifier = styled(
    'p',
)<PackageThemeProps>(({ theme, packageTheme }) => ({
    fontSize: calcRem(14),
    flex: '0 0 auto', // fixes ie11 column wrap bug
    fontWeight: 700,
    lineHeight: 1.167,
    margin: 0,
    color: packageTheme === 'red' ? colors.thewest.ladybird : undefined,
}))

export const StyledSubscriptionPackageActions = styled('div')(({ theme }) => ({
    margin: 'auto 0 0',
    alignSelf: 'center',
    textAlign: 'center',
}))

export const StyledSubscriptionPackageButton = styled(
    Button,
)<PackageThemeProps>(({ theme, packageTheme }) => ({
    margin: calcRem(theme.margins.md, 0, 0),
    backgroundColor:
        packageTheme === 'red' ? colors.thewest.ladybird : undefined,
}))

export const StyledSubscriptionCancellationMessage = styled(
    'p',
)<PackageThemeProps>(({ theme }) => ({
    fontSize: calcRem(11),
    flex: '0 0 auto', // fixes ie11 column wrap bug
    fontWeight: 600,
    lineHeight: 1.167,
    margin: 'auto 0 0',
    color: themedValue(theme, {
        thewest: colors.thewest.greyGorilla,
        perthnow: colors.perthnow.greyBasalt90,
        sevennews: colors.sevennews.charade,
        fallback: undefined,
    }),
    paddingTop: calcRem(theme.margins.xs),
}))

export const Ribbon = styled('p')<PackageThemeProps>(
    ({ theme, packageTheme }) => ({
        position: 'absolute',
        top: 0,
        right: 0,
        background:
            packageTheme === 'red'
                ? colors.thewest.ladybird
                : 'linear-gradient(45deg, #aa01e1 0%,#f400ab 100%)',
        transform: 'rotate(45deg) translate(15%,-100%)',
        height: 80,
        width: 160,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        textTransform: 'uppercase',
        fontSize: 10,
        fontWeight: 700,
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.5)',

        span: {
            color: colors.white,
            display: 'block',
            width: 50,
            textAlign: 'center',
            lineHeight: 1,
            marginBottom: theme.margins.xs,
        },

        svg: {
            position: 'absolute',
            left: '50%',
            transform: 'translate(-50%, calc(-100% - 5px))',
        },
    }),
)

export const StyledSubscribeWithGoogleButton = withClass('swg-button-dark')(
    styled('button')(({ theme }) => ({
        marginTop: theme.margins.md,
        cursor: 'pointer',
    })),
)
